import React, { useState, useEffect, useCallback } from "react";
import { DragDropContext } from "react-beautiful-dnd";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { updateWorkflowStep, updateWorkflowIncolumn } from "../../../../sevices/apiCards";
import KanbanContainor from "../../../../components/Kanban/KanbanContainer";
import OpenCardDetail from "./openCardDetail";
import useToastPromise from "../../../../hooks/useToast";
import useMutate from "../../../../hooks/useMutate";

function KanbanBoard({ workflowSteps, cards, id, boardName, sameWorkFlowIdcards = [] }) {
  const [localCards, setLocalCards] = useState(cards);
  const [originalCards, setOriginalCards] = useState(cards);

  useEffect(() => {
    // Only update if cards have actually changed to prevent unfiltering
    if (JSON.stringify(localCards) !== JSON.stringify(cards)) {
      setLocalCards(cards);
      setOriginalCards(cards);
    }
  }, [cards]);

  const queryClient = useQueryClient();

  const { mutate: updateMutate } = useMutate(
    updateWorkflowStep,
    [],
    () => {
    },
    () => {
      queryClient.invalidateQueries({ queryKey: [`cards`, id] });
    },
    () => {
      setLocalCards(originalCards);
      queryClient.setQueryData(["cards", id], (oldData) => {
        if (!oldData?.data?.responseData) return oldData;
        
        // Maintain the filtered state by only updating the moved card
        const updatedResponseData = oldData.data.responseData.map(card => {
          const originalCard = originalCards.find(oc => oc.card.card_id === card.card.card_id);
          return originalCard || card;
        });
        
        return {
          ...oldData,
          data: {
            ...oldData.data,
            responseData: updatedResponseData,
          }
        };
      });
    }
  );

  const { mutate: reorderMutate } = useMutate(
    updateWorkflowIncolumn, 
    [],
    () =>{
    },
    () => {
      queryClient.invalidateQueries({
        queryKey: [`cards`, id],
      });
    },
    () => {
      setLocalCards(originalCards);
      queryClient.setQueryData(["cards", id], (oldData) => {
        if (!oldData?.data?.responseData) return oldData;
        
        // Maintain the filtered state by only updating the affected cards
        const updatedResponseData = oldData.data.responseData.map(card => {
          const originalCard = originalCards.find(oc => oc.card.card_id === card.card.card_id);
          return originalCard || card;
        });
        
        return {
          ...oldData,
          data: {
            ...oldData.data,
            responseData: updatedResponseData,
          }
        };
      });
    }
  );
  const reorderPayloadRef = React.useRef(null);

  const onDragEnd = useCallback((result) => {
    const { source, destination, draggableId } = result;

    if (!destination) return;
    if (destination.droppableId === 'todo-column') {
      toast.error("Cannot move a Regular task to Quick Tasks");
      return;
    }
    const isSameColumn = source.droppableId === destination.droppableId;
    if (isSameColumn && source.index === destination.index) return;

    // Create a deep copy of the current cards to preserve as original state
    const pristineOriginalCards = JSON.parse(JSON.stringify(localCards));
    setOriginalCards(pristineOriginalCards);

    const destinationCards = localCards
      .filter(card => `${card.card.workflow_step_id}` === destination.droppableId)
      .sort((a, b) => (a?.card?.card_sequence ?? 0) - (b?.card?.card_sequence ?? 0));

    const updated = Array.from(destinationCards);
    let movedCard = localCards.find(card => card.card.card_id === parseInt(draggableId));
    if (!movedCard) return;

    if (!isSameColumn) {
      movedCard = {
        ...movedCard,
        card: {
          ...movedCard.card,
          workflow_step_id: Number(destination.droppableId),
        },
      };
    }

    if (isSameColumn) {
      updated.splice(source.index, 1);
    }
    updated.splice(destination.index, 0, movedCard);

    updated.forEach((card, index) => {
      card.card.card_sequence = index + 1;
    });

    const afterCard = updated[destination.index - 1];
    const nextCard = updated[destination.index + 1];

    const after_card_id = afterCard?.card?.card_id || null;
    const next_card_id = nextCard?.card?.card_id || null;

    const reorderPayload = {
      card_id: draggableId * 1,
      workflow_step_id: destination.droppableId * 1,
      after_card_id,
      next_card_id,
    };

    // Update only the cards that changed while maintaining the filtered state
    const newLocalCards = localCards.map(card => {
      // If this is the moved card, update its workflow step
      if (card.card.card_id === parseInt(draggableId)) {
        return movedCard;
      }
      
      // If this card is in the destination column, update its sequence
      if (`${card.card.workflow_step_id}` === destination.droppableId) {
        const updatedCard = updated.find(u => u.card.card_id === card.card.card_id);
        return updatedCard || card;
      }
      
      return card;
    });

    setLocalCards(newLocalCards);

    // Update the query cache to maintain filtered state
    queryClient.setQueryData(["cards", id], (oldData) => {
      if (!oldData?.data?.responseData) return oldData;

      const existingCards = oldData.data.responseData;

      // Only update the cards that have changed
      const newQueryCards = existingCards.map(card => {
        // If this is the moved card
        if (card.card.card_id === parseInt(draggableId)) {
          return movedCard;
        }
        
        // If this card is in the destination column
        if (`${card.card.workflow_step_id}` === destination.droppableId) {
          const updatedCard = updated.find(u => u.card.card_id === card.card.card_id);
          return updatedCard || card;
        }
        
        return card;
      });

      return {
        ...oldData,
        data: {
          ...oldData.data,
          responseData: newQueryCards,
        }
      };
    });

    if (!isSameColumn) {
      reorderPayloadRef.current = reorderPayload;
      updateMutate({
        card_id: draggableId * 1,
        workflow_step_id: destination?.droppableId * 1,
      });
    } else {
      reorderMutate(reorderPayload);
    }

  }, [localCards, updateMutate, reorderMutate, id, queryClient, originalCards]);

  return (
    <>
      <OpenCardDetail cardsOnBoard={sameWorkFlowIdcards} />
      <DragDropContext onDragEnd={onDragEnd}>
        <KanbanContainor
          key={`${id}-${localCards?.length}`}
          boardName={boardName}
          projects={localCards}
          workflowSteps={workflowSteps}
          boardId={id}
        />
      </DragDropContext>
    </>
  );
}

export default React.memo(KanbanBoard);
